import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Save, Plus, Trash2, Loader, CheckCircle, BookOpen, 
  Play, Download, Upload, AlertCircle, Brain, GraduationCap
} from 'lucide-react';
import UnsplashImagePicker from '@/components/UnsplashImagePicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SimpleLessonGenerator() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showUnsplash, setShowUnsplash] = useState(false);
  const [generatingChapterIndex, setGeneratingChapterIndex] = useState(null); // per-chapter loading
  const [chapterElapsed, setChapterElapsed] = useState(0); // seconds elapsed during generation
  
  // Lesson metadata
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonDescription, setLessonDescription] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');
  const [category, setCategory] = useState('');
  const [tier, setTier] = useState('basic');
  const [selectedClassId, setSelectedClassId] = useState(''); // NEW: Class selection
  
  // Classes list for dropdown
  const [classes, setClasses] = useState([]);
  
  // Chapters (max 5)
  const [chapters, setChapters] = useState([
    { number: 1, title: '', content: '', generated: false, materials: null }
  ]);
  
  // Final Review
  const [finalReviewGenerated, setFinalReviewGenerated] = useState(false);
  const [finalReviewMaterials, setFinalReviewMaterials] = useState(null);
  
  const [finalReviewElapsed, setFinalReviewElapsed] = useState(0); // seconds elapsed during final review
  
  // Draft management
  const [savedSessions, setSavedSessions] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => {
    loadSessions();
    loadClasses();
  }, []);

  const loadSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/simple-lesson/sessions`, {
        withCredentials: true
      });
      setSavedSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadClasses = async () => {
    try {
      const response = await axios.get(`${API}/admin/classes`, {
        withCredentials: true
      });
      setClasses(response.data);
    } catch (error) {
      console.error('Failed to load classes:', error);
      toast.error('Failed to load classes');
    }
  };

  const addChapter = () => {
    if (chapters.length >= 5) {
      toast.error('Maximum 5 chapters allowed');
      return;
    }
    setChapters([...chapters, {
      number: chapters.length + 1,
      title: '',
      content: '',
      generated: false,
      materials: null
    }]);
  };

  const removeChapter = (index) => {
    if (chapters.length === 1) {
      toast.error('Must have at least one chapter');
      return;
    }
    const newChapters = chapters.filter((_, i) => i !== index);
    // Renumber chapters
    newChapters.forEach((ch, idx) => ch.number = idx + 1);
    setChapters(newChapters);
  };

  const updateChapter = (index, field, value) => {
    const newChapters = [...chapters];
    newChapters[index][field] = value;
    setChapters(newChapters);
  };

  const generateChapter = async (index) => {
    const chapter = chapters[index];
    
    if (!chapter.title || !chapter.content) {
      toast.error('Please provide chapter title and content');
      return;
    }

    setGeneratingChapterIndex(index);
    setChapterElapsed(0);
    setLoading(true);

    // Start elapsed timer
    const timerRef = setInterval(() => {
      setChapterElapsed(prev => prev + 1);
    }, 1000);

    try {
      // Step 1: Start the background job (returns immediately — no Cloudflare timeout)
      const startRes = await axios.post(
        `${API}/admin/simple-lesson/generate-chapter`,
        {
          title: chapter.title,
          content: chapter.content,
          chapter_number: chapter.number,
          lesson_id: 'temp_lesson'
        },
        { withCredentials: true, timeout: 15000 }
      );
      const { job_id } = startRes.data;

      // Step 2: Poll for completion. Tolerate a few transient network/timeout hiccups
      // in a row (e.g. brief server load) instead of failing the whole flow on one blip —
      // the background job keeps running on the server regardless of poll failures.
      let attempts = 0;
      let consecutiveFailures = 0;
      const maxAttempts = 60; // 3-min max (60 * 3s)
      const maxConsecutiveFailures = 5;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const statusRes = await axios.get(
            `${API}/admin/simple-lesson/generate-chapter/status/${job_id}`,
            { withCredentials: true, timeout: 15000 }
          );
          consecutiveFailures = 0;
          const job = statusRes.data;
          if (job.status === 'done') {
            const newChapters = [...chapters];
            newChapters[index].generated = true;
            newChapters[index].materials = job.materials;
            setChapters(newChapters);
            toast.success(`Chapter ${chapter.number} generated — 7 study materials created!`);
            return;
          } else if (job.status === 'error') {
            throw new Error(job.error || 'Generation failed on server');
          }
        } catch (pollError) {
          if (pollError.message && pollError.message !== 'Network Error' && !pollError.code) {
            // A genuine "error" status from the server (not a network/timeout blip) — stop.
            throw pollError;
          }
          consecutiveFailures++;
          if (consecutiveFailures >= maxConsecutiveFailures) {
            throw new Error('Lost connection while checking generation status. The chapter may still finish on the server — try reloading or saving the session in a moment.');
          }
        }
        attempts++;
      }
      throw new Error('Generation timed out after 3 minutes. Please try again.');
    } catch (error) {
      console.error('Chapter generation failed:', error);
      const msg = error.code === 'ECONNABORTED'
        ? 'Connection timed out. The server is still generating — please wait a moment and try saving the session.'
        : (error.response?.data?.detail || error.message || 'Failed to generate chapter. Please try again.');
      toast.error(msg, { duration: 8000 });
    } finally {
      clearInterval(timerRef);
      setLoading(false);
      setGeneratingChapterIndex(null);
      setChapterElapsed(0);
    }
  };

  const generateFinalReview = async () => {
    if (chapters.some(ch => !ch.generated)) {
      toast.error('Please generate all chapters first');
      return;
    }

    if (!lessonTitle) {
      toast.error('Please provide a lesson title');
      return;
    }

    setLoading(true);
    setFinalReviewElapsed(0);
    const timerRef = setInterval(() => {
      setFinalReviewElapsed(prev => prev + 1);
    }, 1000);

    try {
      // Step 1: Start background job (returns immediately — no Cloudflare timeout)
      const startRes = await axios.post(
        `${API}/admin/simple-lesson/generate-final-review`,
        {
          lesson_title: lessonTitle,
          chapters: chapters.map(ch => ({
            title: ch.title,
            content: ch.content
          }))
        },
        { withCredentials: true, timeout: 15000 }
      );
      const { job_id } = startRes.data;

      // Step 2: Poll for completion. Tolerate a few transient network/timeout hiccups
      // in a row instead of failing the whole flow on one blip.
      let attempts = 0;
      let consecutiveFailures = 0;
      const maxAttempts = 60; // 3-min max (60 * 3s)
      const maxConsecutiveFailures = 5;
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const statusRes = await axios.get(
            `${API}/admin/simple-lesson/generate-final-review/status/${job_id}`,
            { withCredentials: true, timeout: 15000 }
          );
          consecutiveFailures = 0;
          const job = statusRes.data;
          if (job.status === 'done') {
            setFinalReviewGenerated(true);
            setFinalReviewMaterials(job.materials);
            toast.success('Final Review generated successfully!');
            return;
          } else if (job.status === 'error') {
            throw new Error(job.error || 'Final review generation failed on server');
          }
        } catch (pollError) {
          if (pollError.message && pollError.message !== 'Network Error' && !pollError.code) {
            throw pollError;
          }
          consecutiveFailures++;
          if (consecutiveFailures >= maxConsecutiveFailures) {
            throw new Error('Lost connection while checking generation status. The review may still finish on the server — try reloading in a moment.');
          }
        }
        attempts++;
      }
      throw new Error('Final review generation timed out after 3 minutes. Please try again.');
    } catch (error) {
      console.error('Final review generation failed:', error);
      const msg = error.code === 'ECONNABORTED'
        ? 'Connection timed out. Please try again in a moment.'
        : (error.response?.data?.detail || error.message || 'Failed to generate final review');
      toast.error(msg, { duration: 8000 });
    } finally {
      clearInterval(timerRef);
      setLoading(false);
      setFinalReviewElapsed(0);
    }
  };

  const completeLessonCreation = async () => {
    if (!lessonTitle || !lessonDescription) {
      toast.error('Please provide lesson title and description');
      return;
    }

    if (!selectedClassId) {
      toast.error('Please select a class for this lesson');
      return;
    }

    if (chapters.some(ch => !ch.generated)) {
      toast.error('Please generate all chapters first');
      return;
    }

    if (!finalReviewGenerated) {
      toast.error('Please generate final review first');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/simple-lesson/complete`,
        {
          lesson_title: lessonTitle,
          lesson_description: lessonDescription,
          cover_image_url: coverImageUrl,
          category: category,
          tier: tier,
          class_id: selectedClassId,
          chapters: chapters.map(ch => ({
            title: ch.title,
            content: ch.content,
            materials: ch.materials
          })),
          final_review_materials: finalReviewMaterials
        },
        { withCredentials: true, timeout: 60000 }
      );

      toast.success('Lesson created and added to class successfully!');
      
      // Clear form
      setLessonTitle('');
      setLessonDescription('');
      setCoverImageUrl('');
      setCategory('');
      setTier('basic');
      setSelectedClassId('');
      setChapters([{ number: 1, title: '', content: '', generated: false, materials: null }]);
      setFinalReviewGenerated(false);
      setFinalReviewMaterials(null);

      // Navigate to class detail page
      navigate(`/classes/${selectedClassId}`);
    } catch (error) {
      console.error('Lesson completion failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create lesson');
    } finally {
      setLoading(false);
    }
  };

  const saveSession = async (slot) => {
    if (!lessonTitle) {
      toast.error('Please provide a lesson title before saving');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/simple-lesson/sessions`,
        {
          slot_number: slot,
          lesson_title: lessonTitle,
          lesson_description: lessonDescription,
          cover_image_url: coverImageUrl,
          category: category,
          tier: tier,
          class_id: selectedClassId,
          // Map frontend 'number' → backend 'chapter_number' required by SimpleCourseChapter model
          chapters: chapters.map(ch => ({
            chapter_number: ch.number,
            title: ch.title || '',
            content: ch.content || '',
            generated: ch.generated || false,
            study_guide_id: ch.materials?.study_guide_id || null,
            flashcard_id: ch.materials?.flashcard_id || null,
            matching_id: ch.materials?.matching_id || null,
            memory_id: ch.materials?.memory_id || null,
            fill_in_id: ch.materials?.fill_in_id || null,
            jeopardy_id: ch.materials?.jeopardy_id || null,
            quiz_id: ch.materials?.quiz_id || null,
          })),
          final_review_generated: finalReviewGenerated
        },
        { withCredentials: true }
      );

      toast.success(`Session saved to slot ${slot}`);
      loadSessions();
    } catch (error) {
      console.error('Failed to save session:', error);
      toast.error('Failed to save session: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadSession = (session) => {
    setLessonTitle(session.lesson_title);
    setLessonDescription(session.lesson_description);
    setCoverImageUrl(session.cover_image_url || '');
    setCategory(session.category || '');
    setTier(session.tier);
    setSelectedClassId(session.class_id || '');
    // Map chapter_number → number (backend stores chapter_number, frontend uses number)
    setChapters(session.chapters.map((ch, idx) => ({
      ...ch,
      number: ch.chapter_number || ch.number || (idx + 1)
    })));
    setFinalReviewGenerated(session.final_review_generated);
    setSelectedSlot(session.slot_number);
    toast.success(`Session ${session.slot_number} loaded`);
  };

  const deleteSession = async (slot) => {
    if (!confirm(`Delete session ${slot}?`)) return;

    try {
      await axios.delete(`${API}/admin/simple-lesson/sessions/${slot}`, {
        withCredentials: true
      });
      toast.success(`Session ${slot} deleted`);
      loadSessions();
      if (selectedSlot === slot) {
        setSelectedSlot(null);
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error('Failed to delete session');
    }
  };

  const clearAllTempMaterials = async () => {
    if (!confirm('This will delete ALL temporary lesson materials. Are you sure?')) return;

    try {
      await axios.delete(`${API}/admin/simple-lesson/clear-temp`, {
        withCredentials: true
      });
      toast.success('All temporary materials cleared! You can start fresh.');
      
      // Reset the form
      setLessonTitle('');
      setLessonDescription('');
      setCoverImageUrl('');
      setCategory('');
      setTier('basic');
      setSelectedClassId('');
      setChapters([{ number: 1, title: '', content: '', generated: false, materials: null }]);
      setFinalReviewGenerated(false);
      setFinalReviewMaterials(null);
      setSelectedSlot(null);
    } catch (error) {
      console.error('Failed to clear temp materials:', error);
      toast.error('Failed to clear materials');
    }
  };

  const handleUnsplashSelect = (imageData) => {
    setCoverImageUrl(imageData.url);
    setShowUnsplash(false);
    toast.success(`Image by ${imageData.photographer} selected!`);
  };

  return (
    <Layout>
      <div className="container mx-auto px-6 py-12">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-8 rounded-lg mb-8">
          <div className="flex items-center gap-4 mb-4">
            <GraduationCap className="w-12 h-12" />
            <h1 className="text-5xl font-bold retro-title">Simple Lesson Generator</h1>
          </div>
          <p className="text-xl">Create lessons chapter by chapter with AI-generated study materials</p>
        </div>

        {/* Saved Sessions */}
        <div className="cassette-card p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            <Save className="w-6 h-6" />
            Saved Sessions (5 slots)
          </h2>
          <div className="grid grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map(slot => {
              const session = savedSessions.find(s => s.slot_number === slot);
              return (
                <div
                  key={slot}
                  className={`p-4 border-2 rounded-lg ${
                    session ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'
                  } ${selectedSlot === slot ? 'ring-4 ring-blue-500' : ''}`}
                >
                  <div className="font-bold mb-2">Slot {slot}</div>
                  {session ? (
                    <>
                      <div className="text-sm mb-2 truncate">{session.lesson_title}</div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => loadSession(session)}
                          className="retro-button-small flex-1"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteSession(slot)}
                          className="retro-button-small bg-red-500 hover:bg-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </>
                  ) : (
                    <button
                      onClick={() => saveSession(slot)}
                      className="retro-button-small w-full"
                      disabled={!lessonTitle}
                    >
                      <Upload className="w-4 h-4 inline mr-1" />
                      Save
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t-2 border-gray-300">
            <button
              onClick={clearAllTempMaterials}
              className="retro-button bg-orange-500 hover:bg-orange-600 w-full"
            >
              <Trash2 className="w-5 h-5 inline mr-2" />
              Clear All Temporary Materials
            </button>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Use this to clear old/unwanted lesson materials before starting a new lesson
            </p>
          </div>
        </div>

        {/* Lesson Metadata */}
        <div className="cassette-card p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Lesson Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-bold mb-2 text-[#2C1810]">Lesson Title *</label>
              <input
                type="text"
                value={lessonTitle}
                onChange={(e) => setLessonTitle(e.target.value)}
                placeholder="e.g., Introduction to Acupuncture Points"
                className="w-full p-3 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block font-bold mb-2 text-[#2C1810]">Select Class *</label>
              <select
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
                className="w-full p-3 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              >
                <option value="">-- Select a class --</option>
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>{cls.title}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block font-bold mb-2 text-[#2C1810]">Description *</label>
            <textarea
              value={lessonDescription}
              onChange={(e) => setLessonDescription(e.target.value)}
              placeholder="Brief description of this lesson"
              rows={3}
              className="w-full p-3 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block font-bold mb-2 text-[#2C1810]">Tier</label>
              <select
                value={tier}
                onChange={(e) => setTier(e.target.value)}
                className="w-full p-3 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              >
                <option value="basic">Basic</option>
                <option value="comps_ready">Comp's Ready</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block font-bold mb-2 text-[#2C1810]">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., TCM Fundamentals"
                className="w-full p-3 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
              />
            </div>

            <div>
              <label className="block font-bold mb-2 text-[#2C1810]">Cover Image</label>
              <button
                onClick={() => setShowUnsplash(true)}
                className="retro-button w-full"
              >
                Select Image
              </button>
            </div>
          </div>

          {coverImageUrl && (
            <div className="mt-4">
              <img src={coverImageUrl} alt="Cover" className="w-48 h-48 object-cover rounded" />
            </div>
          )}
        </div>

        {/* Chapters */}
        <div className="cassette-card p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">Chapters (Max 5)</h2>
            <button
              onClick={addChapter}
              disabled={chapters.length >= 5}
              className="retro-button"
            >
              <Plus className="w-5 h-5 inline mr-2" />
              Add Chapter
            </button>
          </div>

          <div className="space-y-6">
            {chapters.map((chapter, index) => (
              <div
                key={index}
                className={`p-6 border-4 rounded-lg ${
                  chapter.generated ? 'border-green-500 bg-green-50' : 'border-[#8B5A3C] bg-white'
                }`}
              >
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold">Chapter {chapter.number}</h3>
                  <div className="flex gap-2">
                    {chapter.generated && <CheckCircle className="w-6 h-6 text-green-600" />}
                    <button
                      onClick={() => removeChapter(index)}
                      className="retro-button-small bg-red-500 hover:bg-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block font-bold mb-2 text-[#2C1810]">Chapter Title *</label>
                    <input
                      type="text"
                      value={chapter.title}
                      onChange={(e) => updateChapter(index, 'title', e.target.value)}
                      placeholder="e.g., The 12 Primary Meridians"
                      disabled={chapter.generated}
                      className="w-full p-3 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
                    />
                  </div>

                  <div>
                    <label className="block font-bold mb-2 text-[#2C1810]">Chapter Content *</label>
                    <textarea
                      value={chapter.content}
                      onChange={(e) => updateChapter(index, 'content', e.target.value)}
                      placeholder="Paste or write the chapter content here..."
                      disabled={chapter.generated}
                      rows={8}
                      className="w-full p-3 border-2 border-[#8B5A3C] rounded text-[#2C1810]"
                    />
                  </div>

                  {!chapter.generated ? (
                    <div>
                      {generatingChapterIndex === index ? (
                        /* Active generation state — visible while waiting */
                        <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <Loader className="w-6 h-6 text-amber-600 animate-spin flex-shrink-0" />
                            <div>
                              <p className="font-bold text-amber-800">
                                Generating Chapter {chapter.number}... ({chapterElapsed}s)
                              </p>
                              <p className="text-sm text-amber-700">
                                Creating 7 study materials (study guide, flashcards, matching, memory, fill-in, jeopardy, test). This takes 30–60 seconds.
                              </p>
                            </div>
                          </div>
                          {/* Progress steps */}
                          <div className="grid grid-cols-7 gap-1 mt-2">
                            {['Study Guide','Flashcards','Matching','Memory','Fill-In','Jeopardy','Test'].map((m, i) => (
                              <div key={m} className="text-center">
                                <div className={`w-full h-2 rounded-full mb-1 ${chapterElapsed > i * 8 ? 'bg-amber-500 animate-pulse' : 'bg-amber-200'}`} />
                                <p className="text-xs text-amber-700 truncate">{m}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => generateChapter(index)}
                          disabled={loading || !chapter.title || !chapter.content}
                          className="retro-button w-full"
                          data-testid={`generate-chapter-btn-${index}`}
                        >
                          <Brain className="w-5 h-5 inline mr-2" />
                          Generate Chapter {chapter.number}
                        </button>
                      )}
                    </div>
                  ) : (
                    /* Success state */
                    <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0" />
                        <div>
                          <p className="font-bold text-green-800 text-lg">Chapter {chapter.number} Generated Successfully!</p>
                          <p className="text-sm text-green-700">All 7 study materials have been saved to the database.</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-7 gap-1">
                        {['Study Guide','Flashcards','Matching','Memory','Fill-In','Jeopardy','Test'].map((m) => (
                          <div key={m} className="text-center">
                            <CheckCircle className="w-4 h-4 text-green-600 mx-auto mb-1" />
                            <p className="text-xs text-green-700 truncate">{m}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Final Review */}
        <div className="cassette-card p-6 mb-8">
          <h2 className="text-2xl font-bold mb-4">Final Review (30-Question Exam)</h2>
          
          {!finalReviewGenerated ? (
            <div>
              <p className="text-gray-700 mb-4">
                Generate a comprehensive 30-question exam covering all chapters.
              </p>
              {loading && generatingChapterIndex === null ? (
                /* Active generation panel — visible while waiting */
                <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Loader className="w-6 h-6 text-amber-600 animate-spin flex-shrink-0" />
                    <div>
                      <p className="font-bold text-amber-800">
                        Generating Final Review... ({finalReviewElapsed}s)
                      </p>
                      <p className="text-sm text-amber-700">
                        Building 30 multiple-choice questions from every chapter. Usually 20–60 seconds.
                      </p>
                    </div>
                  </div>
                  <div className="w-full h-2 rounded-full bg-amber-200 overflow-hidden mt-2">
                    <div
                      className="h-full bg-amber-500 animate-pulse"
                      style={{ width: `${Math.min(100, (finalReviewElapsed / 60) * 100)}%` }}
                    />
                  </div>
                </div>
              ) : (
                <button
                  onClick={generateFinalReview}
                  disabled={loading || chapters.some(ch => !ch.generated)}
                  className="retro-button"
                  data-testid="generate-final-review-btn"
                >
                  <Brain className="w-5 h-5 inline mr-2" />
                  Generate Final Review
                </button>
              )}
              {chapters.some(ch => !ch.generated) && !loading && (
                <p className="text-sm text-amber-700 mt-2">
                  All chapters must be generated before creating the final review.
                </p>
              )}
            </div>
          ) : (
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center gap-3">
              <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0" />
              <div>
                <p className="font-bold text-green-800 text-lg">Final Review Ready!</p>
                <p className="text-sm text-green-700">30-question comprehensive exam saved successfully.</p>
              </div>
            </div>
          )}
        </div>

        {/* Complete Button */}
        <div className="cassette-card p-6">
          <h2 className="text-2xl font-bold mb-4">Complete & Publish Lesson</h2>
          <p className="text-gray-700 mb-4">
            Once all chapters and final review are generated, publish your lesson to the selected class.
          </p>
          
          {!selectedClassId && (
            <div className="bg-yellow-100 border-2 border-yellow-500 rounded p-4 mb-4">
              <AlertCircle className="w-6 h-6 text-yellow-600 inline mr-2" />
              <span className="font-bold text-yellow-700">
                Please select a class above before publishing
              </span>
            </div>
          )}

          <button
            onClick={completeLessonCreation}
            disabled={
              loading || 
              !lessonTitle || 
              !lessonDescription || 
              !selectedClassId ||
              chapters.some(ch => !ch.generated) || 
              !finalReviewGenerated
            }
            className="retro-button w-full text-xl py-4"
          >
            <Play className="w-6 h-6 inline mr-2" />
            Complete & Add to Class
          </button>
        </div>

        {/* Unsplash Picker */}
        {showUnsplash && (
          <UnsplashImagePicker
            isOpen={showUnsplash}
            onClose={() => setShowUnsplash(false)}
            onSelect={handleUnsplashSelect}
          />
        )}
      </div>
    </Layout>
  );
}
